import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { dispatchEvent } from '@core/configs/dispatch';
import { APP_ROUTES } from '@core/routes/app-routes';
import { AwsService, TranslateService } from '__services';
import { getCurrentUser } from 'aws-amplify/auth';
import { Constants } from '~/app/core/configs/constants';
import { HelperService, UserProfileService } from '~/app/core/services';
import { environment } from '__env';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  hide = true;
  isLoading = false;
  errorMsg = '';
  loginForm: FormGroup;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private fb: FormBuilder,
    private awsService: AwsService,
    private translationService: TranslateService,
    private userProfileService: UserProfileService,
    private route: ActivatedRoute
  ) {
    this.loginForm = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(Constants.Validators.PATTERN.email),
          Validators.maxLength(100),
        ],
      ],
      password: ['', [Validators.required, Validators.maxLength(100)]],
    });

    // getCurrentUser().then((user) => {
    //   this.router.navigate([APP_ROUTES.DASHBOARD], { replaceUrl: true });
    // }).catch((err) => {
    // });
  }

  get formControl(): any {
    return this.loginForm.controls;
  }

  ngOnInit(): void {
    const lang = this.route.snapshot.queryParams['lang'];
    if (lang) {
      const langSupport = environment.LANGUAGE.Support as string[];
      if (langSupport.includes(lang)) {
        this.translationService.use(lang);
      }
    }
    this.awsService.currentUser().then(() =>
      this.router.navigate([APP_ROUTES.DASHBOARD], { replaceUrl: true })
    )
  }

  ngOnDestroy(): void {}

  onHubAuth = (res): void => {
    if (res.payload.event === dispatchEvent.SIGNED_IN) {
      this.ngZone.run(() =>
        this.router.navigate([APP_ROUTES.DASHBOARD], { replaceUrl: true })
      );
    } else {
    }
  };

  login(): void {
    this.loginForm.markAllAsTouched();
    if (this.loginForm.valid && !this.isLoading) {
      this.isLoading = true;
      const { email, password } = this.loginForm.value;
      this.awsService
        .login({ username: email.toLowerCase(), password })
        .then((cognitoUser) => {
          this.errorMsg = '';
          console.log(cognitoUser);
          const { isSignedIn, nextStep } = cognitoUser;
          if (isSignedIn) {
            this.awsService.callLogin().subscribe(
              (res) => {
                this.userProfileService.setUserFeature(res.role.modules)
                // localStorage.setItem(
                //   'userFeatures',
                //   JSON.stringify(res.role.modules)
                // );
                this.userProfileService.setUserProfile(res)
                // localStorage.setItem('userProfile', JSON.stringify(res));
                if(res.language) {
                  localStorage.setItem('userLang', res.language);
                  this.translationService.useUserLang();
                }
                // if (!cognitoUser.attributes.phone_number_verified) {
                //   this.awsService.confirmSMS.next(cognitoUser);
                //   this.router.navigate([APP_ROUTES.NEW_PHONE]);
                // } else this.router.navigate([APP_ROUTES.DASHBOARD]);
              },
              (err) => {
                this.isLoading = false;
                this.errorMsg = err.message;
                // this.router.navigate([APP_ROUTES.DASHBOARD]);
              },
              () => {
                this.isLoading = false;
                this.router.navigate([APP_ROUTES.DASHBOARD]);
              }
            );
          } else {
            const { signInStep } = nextStep;
            switch (signInStep) {
              case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                this.router.navigate([APP_ROUTES.NEW_PASSWORD]);
                break;
              case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
                this.awsService.setCached({username: email, password});
                this.router.navigate([APP_ROUTES.CONFIRM_SMS]);
                break;
            }
          }
          return
          if (cognitoUser.challengeName) {
            switch (cognitoUser.challengeName) {
              case 'SMS_MFA':
                this.awsService.confirmSMS.next(cognitoUser);
                this.awsService.loginValue.next(this.loginForm.getRawValue());
                this.router.navigate([APP_ROUTES.CONFIRM_SMS]);
                break;
              case 'SOFTWARE_TOKEN_MFA':
                break;
              case 'NEW_PASSWORD_REQUIRED':
                this.awsService.setNewPassword.next(cognitoUser);
                this.router.navigate([APP_ROUTES.NEW_PASSWORD]);
                break;
              case 'MFA_SETUP':
                break;
            }
          } else {
            this.awsService.callLogin().subscribe(
              (res) => {
                this.userProfileService.setUserFeature(res.role.modules)
                // localStorage.setItem(
                //   'userFeatures',
                //   JSON.stringify(res.role.modules)
                // );
                this.userProfileService.setUserProfile(res)
                // localStorage.setItem('userProfile', JSON.stringify(res));
                if(res.language) {
                  localStorage.setItem('userLang', res.language);
                }
                // if (!cognitoUser.attributes.phone_number_verified) {
                //   this.awsService.confirmSMS.next(cognitoUser);
                //   this.router.navigate([APP_ROUTES.NEW_PHONE]);
                // } else this.router.navigate([APP_ROUTES.DASHBOARD]);
              },
              (err) => {
                this.isLoading = false;
                this.errorMsg = err.message;
                // this.router.navigate([APP_ROUTES.DASHBOARD]);
              },
              () => {
                this.isLoading = false;
                this.router.navigate([APP_ROUTES.DASHBOARD]);
              }
            );
          }
        })
        .catch((err) => {
          this.isLoading = false;
          this.errorMsg = err.message;
        });
    }
  }

  getEmailErrorMessage(): string {
    if (this.formControl.email.hasError('required')) {
      return this.translationService.translate('You must enter a value');
    }
    if (this.formControl.email.hasError('maxlength')) {
      return this.translationService.translate('Value is over {{char}} character', {
        char: this.formControl.email.errors.maxlength.requiredLength,
      });
    }

    return this.formControl.email.hasError('pattern')
      ? this.translationService.translate('Not a valid email')
      : '';
  }

  getPasswordErrorMessage(): string {
    if (this.formControl.password.hasError('required')) {
      return this.translationService.translate('You must enter a value');
    }
    if (this.formControl.password.hasError('maxlength')) {
      return this.translationService.translate('Value is over {{char}} character', {
        char: this.formControl.password.errors.maxlength.requiredLength,
      });
    }
  }

  goToResetPassword(): void {
    this.router.navigate([APP_ROUTES.RESET_PASSWORD]);
  }

  getToday(): string {
    return HelperService.getToday();
  }
}
